import { Component, OnDestroy, ElementRef, OnInit, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { DashboardService } from '../../../services/dashboard.service';
import { GlobalsService } from '../../../services/globals.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { buildDashboardThemeVars } from '../../../shared/dashboard-widget-theme';
import { SalesTargetEditorComponent } from '../../../shared/sales-target-editor/sales-target-editor.component';

interface ChartBar { x: number; y: number; w: number; h: number; color: string; year: string; value: number; }
interface ChartGroup { label: string; showLabel: boolean; x0: number; w: number; cx: number; bars: ChartBar[]; title: string; }
interface ChartTick { y: number; label: string; }

@Component({
	selector: 'app-dashboard-sales-this-last',
	templateUrl: './dashboard-sales-this-last.component.html',
	styleUrls: ['./dashboard-sales-this-last.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardSalesThisLastComponent implements OnInit, OnDestroy {
	@ViewChild('monthView') monthViewRef: ElementRef;
	datepicked: any = false;
	thisoverlast: any = false;
	dayInvoices: any = false;
	displaydetails: any = false;
	overLastStatus: { loading: boolean; day: number; monthname: string; monthnum: number; fromyear: string; toyear: string; viewing: string; source: number; message: string; temp: { year: any; month: any; }; } = {
		loading: false,
		day: 0,
		monthname: '',
		monthnum: 0,
		fromyear: '',
		toyear: '',
		viewing: 'year',
		source: 2,
		message: '',
		temp: { year: '', month: [] }
	};
	viewingInvoice: any = false;
	fetching_day: Subscription;
	fetching_month: Subscription;
	private yearSub: Subscription;
	private configSub: Subscription;
	private userSub: Subscription;
	user: any = null;

	// ── Custom SVG chart (geometry precomputed so the OnPush template stays allocation-free) ──
	chartGroups: ChartGroup[] = [];
	chartTicks: ChartTick[] = [];
	chartSeries: Array<{ year: string; color: string }> = [];
	chartTargetPoints: string | null = null;
	chartTargetMarkers: Array<{ x: number; y: number; label: string }> = [];
	/** Per-month dashed level segments — visible even when only one month has a target. */
	chartTargetSegments: Array<{ x1: number; x2: number; y: number }> = [];

	private static readonly SERIES_COLORS = ['#c8e6c9', '#81c784', '#43a047', '#1b5e20'];
	private static readonly CHART = { W: 1000, H: 300, padL: 70, padR: 20, padT: 16, padB: 28 };

	constructor(
		private dashboardService: DashboardService,
		private globalsService: GlobalsService,
		private globalSearchService: GlobalSearchService,
		private modalService: NgbModal,
		private cdr: ChangeDetectorRef
	) {
		this.configSub = this.globalSearchService.configsubscription.subscribe(r => {
			if (r) {
				this.loadData();
			}
		});
		this.userSub = this.globalSearchService.user.subscribe(user => {
			this.user = user;
			this.cdr.markForCheck();
		});
	}

	ngOnInit() {
		this.defaultThisOverLast();
	}

	ngOnDestroy() {
		this.configSub?.unsubscribe();
		this.yearSub?.unsubscribe();
		this.userSub?.unsubscribe();
		this.fetching_month?.unsubscribe();
		this.fetching_day?.unsubscribe();
	}

	get themeVars(): Record<string, string> {
		return buildDashboardThemeVars('stl', 'success');
	}

	get canEditTargets(): boolean {
		return !this.user?.user?.issalesman;
	}

	get mtdTarget(): number {
		return parseFloat(this.thisoverlast?.mtd_target) || 0;
	}

	get mtdTargetAttainment(): number {
		const target = this.mtdTarget;
		return target > 0 ? (parseFloat(this.thisoverlast?.mtd_dollars) || 0) / target : 0;
	}

	targetFor(year: any, monthnumber: any): number {
		const period = `${year}-${String(monthnumber).padStart(2, '0')}`;
		return parseFloat(this.thisoverlast?.targets?.[period]) || 0;
	}

	yearHasTargets(year: any): boolean {
		const code = year?.qty?.[0]?.year || year?.sales?.[0]?.year;
		if (!code || !this.thisoverlast?.targets) {
			return false;
		}
		return Object.keys(this.thisoverlast.targets).some(p => p.startsWith(code + '-') && parseFloat(this.thisoverlast.targets[p]) > 0);
	}

	yearTargetTotal(year: any): number {
		const code = year?.qty?.[0]?.year || year?.sales?.[0]?.year;
		if (!code || !this.thisoverlast?.targets) {
			return 0;
		}
		return Object.keys(this.thisoverlast.targets)
			.filter(p => p.startsWith(code + '-'))
			.reduce((sum, p) => sum + (parseFloat(this.thisoverlast.targets[p]) || 0), 0);
	}

	attainmentFor(month: any): number | null {
		const target = this.targetFor(month.year, month.monthnumber);
		if (!target) {
			return null;
		}
		return (parseFloat(month.value) || 0) / target;
	}

	openTargetEditor(): void {
		this.modalService.open(SalesTargetEditorComponent, { size: 'lg', animation: false })
			.result.then((result) => {
				if (result === 'saved') {
					this.loadData();
				}
			}, () => {});
	}

	/** Compact Y/M growth chips; null for the oldest year and for empty months
	 *  with no movement. Cached per view since this runs from the template. */
	private _monthTrendsCache = new Map<string, any>();
	monthTrends(monthLabel: string, year: any, yearIndex: number): Array<{ key: string; pct: number; dir: string }> | null {
		const cacheKey = `${this.overLastStatus.viewing}:${yearIndex}:${monthLabel}`;
		if (this._monthTrendsCache.has(cacheKey)) {
			return this._monthTrendsCache.get(cacheKey);
		}
		let result: Array<{ key: string; pct: number; dir: string }> | null = null;
		if (this.thisoverlast?.years && yearIndex !== this.thisoverlast.years.length - 1) {
			const obj = (year.qty || []).find((m: any) => m.label === monthLabel);
			if (obj) {
				const yearPct = obj.monthgrowth === -1 ? 0 : (parseFloat(obj.monthgrowth) || 0);
				const monthPct = obj.monthpreviousgrowth === -1 ? 0 : (parseFloat(obj.monthpreviousgrowth) || 0);
				const hasQty = (parseFloat(obj.value) || 0) !== 0;
				if (hasQty || yearPct !== 0 || monthPct !== 0) {
					const dir = (p: number) => (p > 0 ? 'up' : (p < 0 ? 'down' : 'flat'));
					result = [
						{ key: 'Y', pct: yearPct, dir: dir(yearPct) },
						{ key: 'M', pct: monthPct, dir: dir(monthPct) },
					];
				}
			}
		}
		this._monthTrendsCache.set(cacheKey, result);
		return result;
	}

	loadData() {
		if (!this.overLastStatus) {
			this.defaultThisOverLast();
		}
		this.overLastStatus.loading = true;
		this.cdr.markForCheck();
		this.yearSub = this.dashboardService.getThisYearOverLast({}).subscribe({
			next: (results: any) => {
				this.overLastStatus.temp.year = results;
				this.overLastStatus.loading = false;
				this.thisoverlast = results;
				this.thisoverlast.targets = this.dashboardService.mergeLocalTargets(results.targets, this.user);
				this.thisoverlast.mtd_target = this.dashboardService.currentMonthTarget(this.thisoverlast.targets, results.mtd_target);
				this._monthTrendsCache.clear();
				this.buildChartView();
				this.cdr.markForCheck();
			},
			error: (err: any) => {
				console.error('Sales This/Last failed to load:', err);
				this.overLastStatus.loading = false;
				this.cdr.markForCheck();
			}
		});
	}

	defaultThisOverLast() {
		let prev_data: any;
		if (this.overLastStatus) {
			prev_data = this.overLastStatus.temp;
		}
		const temp = prev_data ? prev_data : { year: '', month: [] };
		this.overLastStatus = {
			loading: false,
			day: 0,
			monthname: '',
			monthnum: 0,
			fromyear: '',
			toyear: '',
			viewing: 'year',
			source: 2,
			message: '',
			temp: temp
		};
	}

	getSaleTotalYear(year: any) {
		if (!year) return 0;
		return year.sales.reduce((acc: number, items: any) => acc + parseFloat(items.value), 0);
	}

	getCostTotalYear(year: any) {
		return year.cost.reduce((acc: number, items: any) => acc + parseFloat(items.value), 0);
	}

	getQtyTotalYear(year: any) {
		return year.qty.reduce((acc: number, items: any) => acc + parseFloat(items.value), 0);
	}

	viewHistory(month: any, year: any) {
		if (parseInt(month, 10) < 10) {
			month = '0' + month;
		}
		this.datepicked = new Date(year + '-' + month + '-15');
		this.modalService.open(this.monthViewRef, { ariaLabelledBy: 'modal-title', size: 'xl', animation: false }).result.then(() => {}, () => {});
		this.cdr.markForCheck();
	}

	// ── Custom SVG chart ─────────────────────────────────────────────────

	/** Years present in the loaded dataset, oldest first. */
	private get seriesYears(): string[] {
		return (this.thisoverlast?.data_sets || []).map((d: any) => String(Array.isArray(d.label) ? d.label[0] : d.label));
	}

	private formatMoneyShort(v: number): string {
		if (v >= 1_000_000) { return '$' + (v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1) + 'M'; }
		if (v >= 1_000) { return '$' + Math.round(v / 1_000) + 'K'; }
		return '$' + Math.round(v);
	}

	/** Round up to a clean axis ceiling (1/2/2.5/5 × power of ten). */
	private niceCeil(v: number): number {
		if (v <= 0) { return 1; }
		const mag = Math.pow(10, Math.floor(Math.log10(v)));
		for (const m of [1, 2, 2.5, 5, 10]) {
			if (m * mag >= v) { return m * mag; }
		}
		return 10 * mag;
	}

	private buildChartView(): void {
		const labels: any[] = this.thisoverlast?.label_sets?.[0] || [];
		const datasets: any[] = this.thisoverlast?.data_sets || [];
		const { W, H, padL, padR, padT, padB } = DashboardSalesThisLastComponent.CHART;
		const plotW = W - padL - padR;
		const plotH = H - padT - padB;
		const baseline = padT + plotH;

		if (!labels.length || !datasets.length) {
			this.chartGroups = [];
			this.chartTicks = [];
			this.chartSeries = [];
			this.chartTargetPoints = null;
			this.chartTargetMarkers = [];
			this.chartTargetSegments = [];
			return;
		}

		const colors = DashboardSalesThisLastComponent.SERIES_COLORS;
		const series = datasets.map((d: any, i: number) => ({
			year: String(Array.isArray(d.label) ? d.label[0] : d.label),
			values: (d.data || []).map((v: any) => Math.max(parseFloat(v) || 0, 0)),
			color: colors[Math.max(colors.length - datasets.length + i, 0)],
		}));
		this.chartSeries = series.map(s => ({ year: s.year, color: s.color }));

		// Target line: the latest year's monthly targets (year view only).
		const latestYear = series[series.length - 1]?.year;
		const isYearView = this.overLastStatus.viewing === 'year' && labels.length === 12;
		const targetVals = isYearView
			? labels.map((_, i) => this.targetFor(latestYear, i + 1))
			: [];

		const rawMax = Math.max(1, ...series.map(s => Math.max(0, ...s.values)), ...targetVals);
		const max = this.niceCeil(rawMax);

		this.chartTicks = [0, 0.25, 0.5, 0.75, 1].map(frac => ({
			y: baseline - frac * plotH,
			label: this.formatMoneyShort(max * frac),
		}));

		const n = labels.length;
		const groupW = plotW / n;
		const innerW = groupW * 0.72;
		const barW = innerW / series.length;
		const labelEvery = n > 16 ? 2 : 1;

		const currency = (v: number) => '$' + v.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		this.chartGroups = labels.map((label, gi) => {
			const x0 = padL + gi * groupW;
			const start = x0 + (groupW - innerW) / 2;
			const bars = series.map((s, si) => {
				const v = s.values[gi] || 0;
				// Keep tiny non-zero months visible (e.g. $800 against a $1.3M axis).
				const h = v > 0 ? Math.max((v / max) * plotH, 2.5) : 0;
				return { x: start + si * barW, y: baseline - h, w: Math.max(barW - 2, 1), h, color: s.color, year: s.year, value: v };
			});
			return {
				label: String(label),
				showLabel: gi % labelEvery === 0,
				x0,
				w: groupW,
				cx: x0 + groupW / 2,
				bars,
				title: `${label} — ` + bars.map(b => `${b.year}: ${currency(b.value)}`).join(' · '),
			};
		});

		// Dashed target line: a level segment across each month with a target
		// (visible even for a single month), connected when 2+ months are set.
		const withTargets = targetVals
			.map((t, i) => ({ t, i }))
			.filter(p => p.t > 0);
		this.chartTargetSegments = withTargets.map(p => ({
			x1: padL + p.i * groupW + 2,
			x2: padL + (p.i + 1) * groupW - 2,
			y: baseline - (p.t / max) * plotH,
		}));
		const markers = withTargets.map(p => ({
			x: padL + p.i * groupW + groupW / 2,
			y: baseline - (p.t / max) * plotH,
			label: `Target ${labels[p.i]}: ${currency(p.t)}`,
		}));
		this.chartTargetMarkers = markers;
		this.chartTargetPoints = markers.length >= 2 ? markers.map(m => `${m.x.toFixed(1)},${m.y.toFixed(1)}`).join(' ') : null;
	}

	onChartGroupClick(index: number): void {
		if (this.overLastStatus.viewing === 'year') {
			this.drillMonth(index);
		} else if (this.overLastStatus.viewing === 'month') {
			this.chartDayClick(index);
		}
	}

	/** Drill from the year chart into day-by-day for the clicked month —
	 *  this year vs last only, not every loaded year. */
	drillMonth(monthIndex: number): void {
		this.fetching_month?.unsubscribe();
		const years = this.seriesYears;
		const data = {
			monthnum: monthIndex + 1,
			fromyear: years[Math.max(years.length - 2, 0)],
			toyear: years[years.length - 1]
		};
		this.overLastStatus.monthname = (new Date(0, data.monthnum, 0)).toLocaleString('default', { month: 'long' });
		this.overLastStatus.monthnum = data.monthnum;
		this.overLastStatus.message = `Loading ${this.overLastStatus.monthname}...`;
		this.overLastStatus.loading = true;
		this.cdr.markForCheck();
		this.fetching_month = this.dashboardService.getThisMonthOverLast(data).subscribe({
			next: (results: any) => {
				try {
					this.overLastStatus.temp.month[0] = results;
					this.overLastStatus.viewing = 'month';
					this.thisoverlast = results;
					this._monthTrendsCache.clear();
					this.buildChartView();
				} catch (e) {
					console.error('Sales This/Last month drill failed to render:', e);
					this.overLastStatus.message = 'Could not display that month';
				}
				this.overLastStatus.loading = false;
				this.cdr.markForCheck();
			},
			error: (err: any) => {
				console.error('Sales This/Last month drill failed:', err);
				this.overLastStatus.loading = false;
				this.overLastStatus.message = '';
				this.cdr.markForCheck();
			}
		});
	}

	/** Drill from the month chart into the invoice list for the clicked day. */
	chartDayClick(dayIndex: number): void {
		const years = this.seriesYears;
		this.getDaySalesRange(dayIndex + 1, years[Math.max(years.length - 2, 0)], years[years.length - 1]);
	}

	/** Day drill from the month table buttons (single year). */
	getDaySales(day: any, year: any, _legacy: any): void {
		this.getDaySalesRange(day, String(year), String(year));
	}

	private getDaySalesRange(day: number, fromyear: string, toyear: string): void {
		if (this.fetching_day) {
			this.fetching_day.unsubscribe();
			this.overLastStatus.loading = false;
			this.overLastStatus.message = '';
		}
		this.overLastStatus.temp.month[1] = JSON.parse(JSON.stringify(this.overLastStatus));
		this.overLastStatus.fromyear = fromyear;
		this.overLastStatus.toyear = toyear;
		this.overLastStatus.monthnum = new Date(Date.parse(`${this.overLastStatus.monthname} 1,2013`)).getMonth() + 1;
		this.overLastStatus.day = day;
		this.overLastStatus.message = `Loading ${this.overLastStatus.monthname} ${this.overLastStatus.day}, ${fromyear === toyear ? fromyear : `${fromyear} - ${toyear}`}`;
		this.overLastStatus.loading = true;
		this.cdr.markForCheck();
		this.fetching_day = this.dashboardService.getThisDayOverLast(this.overLastStatus).subscribe({
			next: (results: any) => {
				try {
					if (results?.length) {
						this.openDocument(results[0].orderno, results[0].type, results[0].id, 'details', 'pdf');
						this.dayInvoices = results;
						this.overLastStatus.viewing = 'day';
					} else {
						this.overLastStatus.message = 'No invoices for that day';
					}
				} catch (e) {
					console.error('Sales This/Last day drill failed to render:', e);
					this.overLastStatus.message = 'Could not display that day';
				}
				this.overLastStatus.loading = false;
				this.cdr.markForCheck();
			},
			error: (err: any) => {
				console.error('Sales This/Last day drill failed:', err);
				this.overLastStatus.loading = false;
				this.overLastStatus.message = '';
				this.cdr.markForCheck();
			}
		});
	}

	overLastBack() {
		this._monthTrendsCache.clear();
		const viewing = this.overLastStatus.viewing;
		switch (viewing) {
			case 'month':
				this.defaultThisOverLast();
				this.thisoverlast = this.overLastStatus.temp.year;
				break;
			case 'day':
				this.thisoverlast = this.overLastStatus.temp.month[0];
				this.overLastStatus = this.overLastStatus.temp.month[1];
				this.dayInvoices = false;
				this.viewingInvoice = false;
				break;
		}
		this.buildChartView();
		this.cdr.markForCheck();
	}

	openDocument(orderno: any, type: any, id: any, content: any, display: string) {
		switch (type) {
			case '11':
				this.globalsService.getCreditNote(id, display).subscribe((result: any) => {
					this.displaydetails = result;
					this.cdr.markForCheck();
				});
				break;
			case '10':
				this.globalsService.getInvoice(id, display).subscribe((result: any) => {
					this.displaydetails = result;
					this.cdr.markForCheck();
				});
				break;
		}
		this.viewingInvoice = { id: id, orderno: orderno };
		this.cdr.markForCheck();
	}
}
